import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Tag, Trash2, BarChart3, Copy, CheckCircle, Percent, Euro, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [validateCode, setValidateCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', coupon_type: 'percent', discount_value: 10,
    min_purchase: '', max_uses: 100, valid_from: '', valid_until: '', price: '', image: ''
  });
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    try {
      const res = await fetch(`${API}/business/coupons`, { headers });
      if (res.ok) setCoupons(await res.json());
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      discount_value: parseFloat(form.discount_value),
      min_purchase: form.min_purchase ? parseFloat(form.min_purchase) : null,
      max_uses: parseInt(form.max_uses),
      price: parseFloat(form.price),
    };
    try {
      const url = editCoupon ? `${API}/business/coupons/${editCoupon.id}` : `${API}/business/coupons`;
      const method = editCoupon ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) {
        toast.success(editCoupon ? 'Coupon mis a jour' : 'Coupon cree');
        setShowForm(false); setEditCoupon(null);
        resetForm(); fetchCoupons();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur reseau'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Desactiver ce coupon ?')) return;
    await fetch(`${API}/business/coupons/${id}`, { method: 'DELETE', headers });
    toast.success('Coupon desactive');
    fetchCoupons();
  };

  const handleValidate = async () => {
    if (!validateCode.trim()) return;
    setValidating(true); setValidationResult(null);
    try {
      const res = await fetch(`${API}/business/coupons/validate/${validateCode.trim()}`, { method: 'POST', headers });
      const data = await res.json();
      if (res.ok) { setValidationResult({ valid: true, ...data }); toast.success(data.message); }
      else { setValidationResult({ valid: false, message: data.detail }); toast.error(data.detail); }
    } catch { toast.error('Erreur de validation'); }
    finally { setValidating(false); }
  };

  const resetForm = () => setForm({
    title: '', description: '', coupon_type: 'percent', discount_value: 10,
    min_purchase: '', max_uses: 100, valid_from: '', valid_until: '', price: '', image: ''
  });

  const openEdit = (c) => {
    setEditCoupon(c);
    setForm({
      title: c.title, description: c.description || '', coupon_type: c.coupon_type,
      discount_value: c.discount_value, min_purchase: c.min_purchase || '',
      max_uses: c.max_uses, valid_from: c.valid_from, valid_until: c.valid_until,
      price: c.price, image: c.image || ''
    });
    setShowForm(true);
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b sticky top-16 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/business/dashboard"><ArrowLeft className="w-5 h-5 text-charcoal" /></Link>
            <h1 className="font-bold text-charcoal text-lg">Mes Coupons</h1>
          </div>
          <Button size="sm" onClick={() => { resetForm(); setEditCoupon(null); setShowForm(true); }} data-testid="create-coupon-btn">
            <Plus className="w-4 h-4 mr-1" /> Creer
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Commission info */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <strong>Commission Gusto : 10%</strong> sur chaque coupon vendu. Vous recevez 90% du prix de vente.
        </div>

        {/* Validate Coupon */}
        <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="validate-coupon-section">
          <h2 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" /> Valider un coupon client
          </h2>
          <div className="flex gap-2">
            <input
              type="text" placeholder="Code GUSTO-XXXXXXXX" value={validateCode}
              onChange={e => setValidateCode(e.target.value.toUpperCase())}
              className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="validate-code-input"
            />
            <Button onClick={handleValidate} disabled={validating} data-testid="validate-code-btn">
              {validating ? 'Verification...' : 'Valider'}
            </Button>
          </div>
          {validationResult && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${validationResult.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {validationResult.valid ? validationResult.message : validationResult.message}
            </div>
          )}
        </div>

        {/* Create / Edit Form */}
        {showForm && (
          <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="coupon-form">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-charcoal">{editCoupon ? 'Modifier' : 'Nouveau'} coupon</h2>
              <button onClick={() => { setShowForm(false); setEditCoupon(null); }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-charcoal">Titre du coupon *</label>
                <input type="text" required value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" placeholder="Ex: -20% sur votre commande" data-testid="coupon-title-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-charcoal">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" rows={2} placeholder="Details du coupon..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-charcoal">Type de reduction *</label>
                  <select value={form.coupon_type} onChange={e => setForm({...form, coupon_type: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" data-testid="coupon-type-select">
                    <option value="percent">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (EUR)</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-charcoal">Valeur reduction *</label>
                  <div className="relative mt-1">
                    <input type="number" required min="1" step="0.01" value={form.discount_value}
                      onChange={e => setForm({...form, discount_value: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10" data-testid="coupon-discount-input" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      {form.coupon_type === 'percent' ? '%' : 'EUR'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-charcoal">Prix de vente du coupon *</label>
                  <div className="relative mt-1">
                    <input type="number" required min="0.50" step="0.01" value={form.price}
                      onChange={e => setForm({...form, price: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10" data-testid="coupon-price-input" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">EUR</span>
                  </div>
                  {form.price && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Vous recevrez {(parseFloat(form.price) * 0.9).toFixed(2)} EUR (commission Gusto : {(parseFloat(form.price) * 0.1).toFixed(2)} EUR)
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-charcoal">Achat min.</label>
                  <input type="number" min="0" step="0.01" value={form.min_purchase}
                    onChange={e => setForm({...form, min_purchase: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" placeholder="Optionnel" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-charcoal">Valide du *</label>
                  <input type="date" required value={form.valid_from} onChange={e => setForm({...form, valid_from: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" data-testid="coupon-from-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-charcoal">Jusqu'au *</label>
                  <input type="date" required value={form.valid_until} onChange={e => setForm({...form, valid_until: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" data-testid="coupon-until-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-charcoal">Nb max.</label>
                  <input type="number" min="1" value={form.max_uses} onChange={e => setForm({...form, max_uses: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" />
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="coupon-submit-btn">
                {editCoupon ? 'Mettre a jour' : 'Creer le coupon'}
              </Button>
            </form>
          </div>
        )}

        {/* Coupons List */}
        {coupons.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm">
            <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold text-charcoal mb-1">Aucun coupon</h3>
            <p className="text-sm text-muted-foreground">Creez votre premier coupon promotionnel pour attirer des clients.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {coupons.map(c => (
              <div key={c.id} className={`bg-white rounded-xl p-4 shadow-sm border-l-4 ${c.is_active ? 'border-l-green-500' : 'border-l-gray-300'}`} data-testid={`coupon-card-${c.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-charcoal flex items-center gap-2">
                      {c.coupon_type === 'percent' ? <Percent className="w-4 h-4 text-primary" /> : <Euro className="w-4 h-4 text-primary" />}
                      {c.title}
                    </h3>
                    {c.description && <p className="text-sm text-muted-foreground mt-1">{c.description}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                    {c.is_active && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Reduction</span>
                    <p className="font-semibold">{c.discount_value}{c.coupon_type === 'percent' ? '%' : ' EUR'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Prix</span>
                    <p className="font-semibold">{c.price} EUR</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Vendus</span>
                    <p className="font-semibold">{c.purchases_count || 0} / {c.max_uses}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Revenu</span>
                    <p className="font-semibold text-green-600">{c.total_revenue || 0} EUR</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <span>Du {c.valid_from} au {c.valid_until}</span>
                  {!c.is_active && <span className="text-red-500 font-medium">Desactive</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
